import { ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'v5/design-system/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger
} from 'v5/design-system/DropdownMenu';

export interface SizeMenuOption<T extends string> {
  value: T;
  label: string;
  /** A small picture of the size, shown in the menu and on the button. */
  icon: ReactNode;
}

export interface SizeMenuProps<T extends string> {
  /** What is being sized (“Max tile size”, “Card size”): the menu's heading and the button's name. */
  label: string;
  value: T;
  /** The sizes the host offers, smallest first. */
  options: ReadonlyArray<SizeMenuOption<T>>;
  onChange: (value: T) => void;
}

/**
 * A “Medium ▾” menu of named sizes under the host's heading; the button is named “Max tile size:
 * Medium” for assistive tech. In a narrow toolbar (`@container/toolbar` under 48rem) the button
 * keeps the size's picture only.
 */
export function SizeMenu<T extends string>({
  label,
  value,
  options,
  onChange
}: SizeMenuProps<T>) {
  const { t } = useTranslation('patterns', { keyPrefix: 'SizeMenu' });
  const current = options.find(option => option.value === value);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="tertiary"
          size="sm"
          className="gap-2"
          aria-label={t('trigger', { label, value: current?.label ?? value })}
        >
          {current?.icon}
          <span className="@max-3xl/toolbar:sr-only">
            {current?.label ?? value}
          </span>
          <ChevronDown />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={value}
          onValueChange={next => {
            const picked = options.find(option => option.value === next);
            if (picked) onChange(picked.value);
          }}
        >
          {options.map(option => (
            <DropdownMenuRadioItem key={option.value} value={option.value}>
              {option.icon}
              {option.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
